import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import './App.css';

import AuthPage from './pages/AuthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuctioneerLoginPage from './pages/AuctioneerLoginPage';
import EventsPage from './pages/EventsPageV2';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import CreatePage from './pages/CreatePage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import EventDetailPage from './pages/EventDetailPageV2';
import TournamentDetailPage from './pages/TournamentDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import EventCheckoutPage from './pages/EventCheckoutPage';
import BookingConfirmationPage from './pages/BookingConfirmationPage';
import MembershipPage from './pages/MembershipPage';
import TicketsPage from './pages/TicketsPage';
import TournamentRegisterPage from './pages/TournamentRegisterPage';
import LiveScoringPage from './pages/LiveScoringPage';
import MatchDetailPage from './pages/MatchDetailPage';
import NotificationsPage from './pages/NotificationsPage';
import LegalPage from './pages/LegalPage';
import CheckInPage from './pages/CheckInPage';
import LogoLoader from './components/LogoLoader';
import { initNativeBridge } from './lib/native';

// Mount once inside BrowserRouter — wires Capacitor deep links + back button.
function NativeBootstrap() {
  const navigate = useNavigate();
  useEffect(() => {
    initNativeBridge({ navigate });
    // initNativeBridge guards against double-init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();

  // Hide bottom nav on auth-related pages
  if (location.pathname === '/auth' || 
      location.pathname === '/forgot-password' || 
      location.pathname === '/reset-password') return null;

  const isEventDetail = location.pathname.startsWith('/events/') && location.pathname.split('/').length > 2;
  const isProductDetail = location.pathname.startsWith('/shop/') && location.pathname.split('/').length > 2;
  const isTournamentReg = location.pathname.includes('/register');
  const shouldHide = isEventDetail || isProductDetail || isTournamentReg;

  const isConnect = location.pathname.startsWith('/events');
  const isShop = location.pathname.startsWith('/shop') || location.pathname.startsWith('/cart') || location.pathname.startsWith('/checkout');
  // const isCreate = location.pathname.startsWith('/create'); // Create section temporarily disabled
  const isProfile = location.pathname.startsWith('/profile') || location.pathname.startsWith('/memberships') || location.pathname.startsWith('/admin') || location.pathname.startsWith('/tickets');

  return (
    <div className={`bottom-nav ${shouldHide ? 'bottom-nav-hidden' : ''}`} data-testid="bottom-nav">
      <button
        data-testid="nav-connect"
        onClick={() => navigate('/events')}
        className={`bottom-nav-item ${isConnect ? 'active' : ''}`}
      >
        <i className={`${isConnect ? 'ph-fill' : 'ph-bold'} ph-compass text-xl`}></i>
        <span>Connect</span>
      </button>
      <button
        data-testid="nav-shop"
        onClick={() => navigate('/shop')}
        className={`bottom-nav-item ${isShop ? 'active' : ''}`}
      >
        <i className={`${isShop ? 'ph-fill' : 'ph-bold'} ph-storefront text-xl`}></i>
        <span>Shop</span>
      </button>
      {/* Create tab temporarily disabled — uncomment to restore.
      <button
        data-testid="nav-create"
        onClick={() => navigate('/create')}
        className={`bottom-nav-item ${isCreate ? 'active' : ''}`}
      >
        <i className={`${isCreate ? 'ph-fill' : 'ph-bold'} ph-note-pencil text-xl`}></i>
        <span>Create</span>
      </button>
      */}
      <button
        data-testid="nav-profile"
        onClick={() => navigate('/profile')}
        className={`bottom-nav-item ${isProfile ? 'active' : ''}`}
      >
        <i className={`${isProfile ? 'ph-fill' : 'ph-bold'} ph-user text-xl`}></i>
        <span>Profile</span>
      </button>
    </div>
  );
}

function PrivateRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/auth" />;
}

function App() {
  const [isBooting, setIsBooting] = useState(true);

  useEffect(() => {
    const hasSeenLoader = sessionStorage.getItem('byard-loader-seen');
    if (hasSeenLoader) {
      setIsBooting(false);
      return;
    }
    const timer = setTimeout(() => {
      sessionStorage.setItem('byard-loader-seen', 'true');
      setIsBooting(false);
    }, 4200); // Extended for smoother, calmer animation
    return () => clearTimeout(timer);
  }, []);

  if (isBooting) {
    return <LogoLoader />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
        <NotificationProvider>
        <NativeBootstrap />
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auctioneer-login" element={<AuctioneerLoginPage />} />
          <Route path="/auctioneer-login/:eventId" element={<AuctioneerLoginPage />} />
          <Route
            path="/events"
            element={
              <PrivateRoute>
                <EventsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/events/:id"
            element={
              <PrivateRoute>
                <EventDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/tournaments/:id"
            element={
              <PrivateRoute>
                <TournamentDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/tournaments/:eventId/live-scoring/:fixtureId"
            element={
              <PrivateRoute>
                <LiveScoringPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/tournaments/:eventId/match/:fixtureId"
            element={<MatchDetailPage />}
          />
          <Route
            path="/events/:id/checkout"
            element={
              <PrivateRoute>
                <EventCheckoutPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/bookings/:id"
            element={
              <PrivateRoute>
                <BookingConfirmationPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/shop"
            element={
              <PrivateRoute>
                <ShopPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/shop/:id"
            element={
              <PrivateRoute>
                <ProductDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/shop/services/:id"
            element={
              <PrivateRoute>
                <ServiceDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/cart"
            element={
              <PrivateRoute>
                <CartPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/checkout"
            element={
              <PrivateRoute>
                <CheckoutPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/create"
            element={
              <PrivateRoute>
                <CreatePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/memberships"
            element={
              <PrivateRoute>
                <MembershipPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/tickets"
            element={
              <PrivateRoute>
                <TicketsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/events/:eventId/register"
            element={
              <PrivateRoute>
                <TournamentRegisterPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <AdminPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/check-in"
            element={
              <PrivateRoute>
                <CheckInPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <NotificationsPage />
              </PrivateRoute>
            }
          />
          <Route path="/legal/:slug" element={<LegalPage />} />
          <Route path="/legal" element={<Navigate to="/legal/privacy" replace />} />
          <Route path="/" element={<Navigate to="/events" />} />
        </Routes>
        <BottomNav />
        <Toaster position="top-center" richColors={false} />
        </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;